import { Box } from "@mui/material";
import { KeyboardArrowUp as UpIcon } from "@mui/icons-material";
import { macro } from "../ui/macroTokens";
import { v2, nums } from "../ui/v2Tokens";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { inDisplayUnit, remainingLabel, targetStatus } from "./targetsMath";
import type { MacroSet, MacroTotals } from "../model/nutritionV2Model";

type Props = {
  totals: MacroTotals;
  targets: MacroSet;
  /** takes the trainer back to the full targets grid, which lives at the top of the page */
  onExpand: () => void;
};

/**
 * The two numbers a trainer builds against, as one line.
 *
 * ── why this exists ──
 *
 * On a phone the full `TargetsSummaryBar` is a five-cell grid that comes to roughly 290px, and it
 * used to be PINNED underneath a header that itself wrapped to four rows. Together they took about
 * 670px of a 667px screen: the chrome that exists to help you read the menu was covering all of it.
 * So below `md` the grid stops pinning and goes back into the flow at the top of the page, and this
 * takes its place in the sticky band — the same question ("how much is still missing?") answered in
 * a twenty-fifth of the height.
 *
 * ── why only two macros ──
 *
 * Calories and protein. Not because the others do not matter, but because a line that lists five
 * wraps to three, and a three-line strip is a small version of the problem it was added to solve.
 * These two are what a menu is actually built against — the rest follows from them, and the full
 * grid is one tap away.
 */
const TargetsMiniStrip = ({ totals, targets, onExpand }: Props) => {
  const mode = useNutritionDisplayMode();
  // judged on the figures the strip prints, as the full grid does — see `inDisplayUnit`
  const calories = targetStatus(
    inDisplayUnit(totals.calories, "calories", mode),
    inDisplayUnit(targets.calories, "calories", mode)
  );
  const protein = targetStatus(
    inDisplayUnit(totals.protein, "protein", mode),
    inDisplayUnit(targets.protein, "protein", mode)
  );

  /** An overshoot is the one state worth colouring: everything else is progress. */
  const tone = (over: boolean) => (over ? v2.over : v2.ink);

  return (
    <Box
      component="button"
      type="button"
      data-testid="targets-mini-strip"
      aria-label="הצג את היעדים המלאים"
      onClick={onExpand}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
        minHeight: "34px",
        padding: "5px 10px",
        borderRadius: v2.rSm,
        border: `1px solid ${v2.line}`,
        background: v2.surface,
        color: v2.ink2,
        fontFamily: "inherit",
        fontSize: "12.5px",
        textAlign: "start",
        cursor: "pointer",
        ...nums,
      }}
    >
      <Box
        component="span"
        sx={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0, flex: 1 }}
      >
        <Box component="span" sx={{ width: "6px", height: "6px", borderRadius: "2px", background: v2.brand, flex: "none" }} />
        <Box component="span" data-testid="mini-strip-calories" sx={{ fontWeight: 600, color: tone(calories.over) }}>
          {remainingLabel(totals.calories, targets.calories, { unit: "", mode })}
        </Box>

        <Box component="span" sx={{ color: v2.ink4, flex: "none" }}>
          ·
        </Box>

        <Box component="span" sx={{ width: "6px", height: "6px", borderRadius: "2px", background: macro.protein.text, flex: "none" }} />
        <Box
          component="span"
          data-testid="mini-strip-protein"
          sx={{ fontWeight: 600, color: tone(protein.over), whiteSpace: "nowrap" }}
        >
          {/* the macro is named, because "נותרו 48" beside a calorie figure is unreadable without it */}
          {/* in points the label already carries its unit — no grams sign after a points figure */}
          {remainingLabel(totals.protein, targets.protein, { mode, metric: "protein" })}
          {" "}חלבון
        </Box>
      </Box>

      {/* The affordance. Without it the strip reads as a label rather than as something to press —
          and it is the only way back to the numbers it is summarising. */}
      <UpIcon sx={{ fontSize: 16, color: v2.ink4, flex: "none" }} />
    </Box>
  );
};

export default TargetsMiniStrip;
